import React from "react";
import { api } from "@/lib/api";

const COLOR_MAP = {
  blue: "bg-cef-blue text-white",
  pink: "bg-cef-pink text-white",
  yellow: "bg-cef-yellow text-black",
  green: "bg-cef-green text-black",
  black: "bg-black text-white",
};

/**
 * Renders a title containing `[word](color)` tokens as a list of React nodes
 * where the highlighted word is wrapped in a colored box.
 * Supported colors: blue, pink, yellow, green, black.
 */
export function renderHighlightedTitle(text) {
  if (!text) return null;
  const parts = [];
  const regex = /\[([^\]]+)\]\((blue|pink|yellow|green|black)\)/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const cls = COLOR_MAP[match[2]] || "bg-cef-yellow text-black";
    parts.push(
      <span key={key++} className={`${cls} px-2 border-2 border-black inline-block -rotate-1`}>{match[1]}</span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

/**
 * PageHeader — Titre + sous-titre éditables via Admin → SiteConfig → En-têtes de pages.
 * Le `slug` correspond à la clé dans `site_config.page_headers`.
 * Les `fallbackTitle` / `fallbackSubtitle` s'affichent tant que la config charge ou
 * si le slug n'existe pas dans la config.
 */
export default function PageHeader({ slug, fallbackTitle = "", fallbackSubtitle = "" }) {
  const [hdr, setHdr] = React.useState(null);
  React.useEffect(() => {
    api.get("/site-config")
      .then((r) => setHdr(r.data?.page_headers?.[slug] || null))
      .catch(() => {});
  }, [slug]);

  const title = hdr?.title ?? fallbackTitle;
  const subtitle = hdr?.subtitle ?? fallbackSubtitle;

  return (
    <header data-testid={`page-header-${slug}`}>
      <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tighter uppercase" data-testid={`page-header-title-${slug}`}>
        {renderHighlightedTitle(title)}
      </h1>
      {subtitle && (
        <p className="mt-3 text-neutral-700 max-w-2xl" data-testid={`page-header-subtitle-${slug}`}>
          {subtitle}
        </p>
      )}
    </header>
  );
}
